import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import * as Form from '@radix-ui/react-form';

import { useSearch } from './AutoSuggest.hook'; // Assuming this hook is defined in the same directory
import Icon from '@/elements/Icon/Icon.component'; // Assuming Icon component exists
import Button from '@/elements/Button/Button.component'; // For the submit button

import { IProps } from './AutoSuggest.interface'; // Assuming this interface is defined in the same directory
import '@/components/AutoSuggest/AutoSuggest.styles.css'; // Assuming styles are defined in this file

const AutoSuggestInput: React.FC<IProps> = ({
    name,
    label,
    apiUrl,
    placeholder = 'Search...',
    onSearchSubmit,
    onSuggestionSelect,
    className,
    initialValue = '',
    buttonLabel = 'Search',
    buttonLocation = 'internal',
    viewAllCta
}) => {
    const [searchTerm, setSearchTerm] = useState(initialValue);
    const { suggestions, isLoading } = useSearch(apiUrl, searchTerm);
    const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsListRef = useRef<HTMLUListElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const currentQuery = formData.get(name) as string;
        if (currentQuery) {
            onSearchSubmit(currentQuery);
        }
        setIsSuggestionsVisible(false);
        inputRef.current?.blur();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = event.target.value;
        setSearchTerm(newSearchTerm);
        setIsSuggestionsVisible(newSearchTerm.length > 0);
        setActiveIndex(-1);
    };

    const handleSuggestionClick = (suggestion: string) => {
        setSearchTerm(suggestion);
        setIsSuggestionsVisible(false);
        setActiveIndex(-1);
        if (onSuggestionSelect) {
            onSuggestionSelect(suggestion);
        }
        // Trigger form submission with the selected suggestion's label
        // Update input value directly before submitting if needed
        if (inputRef.current) {
            inputRef.current.value = suggestion; // Ensure input has the selected value
        }
        if (formRef.current) {
            // Create a new FormData with the updated value
            const formData = new FormData(formRef.current);
            formData.set(name, suggestion);

            // Manually call onSearchSubmit as requestSubmit might not pick up the immediate state change
            onSearchSubmit(suggestion);
        }
        inputRef.current?.blur();
    };

    const handleInputFocus = () => {
        if (searchTerm.length > 0 && suggestions.length > 0) {
            setIsSuggestionsVisible(true);
        }
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // Delay hiding suggestions to allow click on a suggestion item
        if (suggestionsListRef.current?.contains(event.relatedTarget as Node)) {
            return;
        }
        setTimeout(() => {
            setIsSuggestionsVisible(false);
        }, 150);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isSuggestionsVisible || suggestions.length === 0) {
            if (event.key === 'Enter' && formRef.current) {
                // Allow form submission if suggestions are not visible or empty
            }
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex(prev => (prev + 1) % suggestions.length);
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
                break;
            case 'Enter':
                event.preventDefault();
                if (activeIndex >= 0 && activeIndex < suggestions.length) {
                    handleSuggestionClick(suggestions[activeIndex]);
                } else if (formRef.current) {
                    formRef.current.requestSubmit();
                }
                break;
            case 'Escape':
                event.preventDefault();
                setIsSuggestionsVisible(false);
                setActiveIndex(-1);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (activeIndex >= 0 && suggestionsListRef.current) {
            const activeItem = suggestionsListRef.current.children[activeIndex] as HTMLLIElement;
            activeItem?.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    useEffect(() => {
        setSearchTerm(initialValue);
    }, [initialValue]);

    return (
        <Form.Root
            ref={formRef}
            className={clsx('autoSuggestInput', className)}
            onSubmit={handleFormSubmit}
        >
            <Form.Field name={name} className="autoSuggestInput__field">
                <div className="autoSuggestInput__inputContainer">
                    <Form.Label htmlFor={`autosuggest-input-${name}`} className="autoSuggestInput__label u-sr-only">
                        {label}
                    </Form.Label>
                    <Form.Control asChild>
                        <input
                            ref={inputRef}
                            id={`autosuggest-input-${name}`}
                            type="search"
                            value={searchTerm}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            className="autoSuggestInput__input"
                            autoComplete="off"
                            aria-autocomplete="list"
                            aria-controls={isSuggestionsVisible && suggestions.length > 0 ? `autosuggest-suggestions-${name}` : undefined}
                            aria-expanded={isSuggestionsVisible && suggestions.length > 0 ? 'true' : undefined}
                            aria-activedescendant={activeIndex >= 0 ? `suggestion-item-${name}-${activeIndex}` : undefined}
                        />
                    </Form.Control>
                    {buttonLocation === 'internal' ? <Form.Submit asChild>
                        {/* Using a simple button here, can be customized */}
                        <Button
                            type="submit"
                            variant="secondary" // Or a specific icon variant
                            className="autoSuggestInput__submitButton"
                            aria-label="Search"
                            color='brand-1'
                        >
                            <Icon icon='magnifying-glass' />
                        </Button>
                    </Form.Submit> : null}
                </div>
                {isSuggestionsVisible && searchTerm.length > 0 && (
                    <ul
                        ref={suggestionsListRef}
                        id={`autosuggest-suggestions-${name}`}
                        className="autoSuggestInput__suggestionsList"
                        role="listbox"
                        aria-labelledby={`autosuggest-input-${name}`}
                    >
                        {isLoading && (
                            <li className="autoSuggestInput__suggestionItem autoSuggestInput__suggestionItem--status" role="status">
                                Loading...
                            </li>
                        )}
                        {!isLoading && suggestions.length === 0 && searchTerm.length > 0 && (
                            <li className="autoSuggestInput__suggestionItem autoSuggestInput__suggestionItem--status">
                                No results found for "{searchTerm}"
                            </li>
                        )}
                        {!isLoading &&
                            suggestions.map((suggestion, index) => (
                                <li
                                    key={`suggestion-${index}`}
                                    id={`suggestion-item-${name}-${index}`}
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={clsx(
                                        'autoSuggestInput__suggestionItem',
                                        index === activeIndex && 'autoSuggestInput__suggestionItem--active'
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSuggestionClick(suggestion)
                                    }}
                                    onMouseDown={(e) => e.preventDefault()} // Prevents input blur before click
                                >
                                    {suggestion}
                                </li>
                            ))}
                        {!isLoading && viewAllCta && !!suggestions.length && (
                            <li className="autoSuggestInput__suggestionItem autoSuggestInput__suggestionItem--viewAll">
                                <Button
                                    {...viewAllCta}
                                    className="autoSuggestInput__viewAllButton"
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.preventDefault();
                                        setIsSuggestionsVisible(false);
                                        inputRef.current?.blur();
                                        if (viewAllCta.onClick) {
                                                const formData = new FormData(formRef.current as HTMLFormElement);
                                                const currentQuery = formData.get(name) as string;
                                            viewAllCta.onClick(e, currentQuery);
                                        }
                                    }}
                                >
                                    {viewAllCta?.children || 'See all results'}
                                </Button>
                            </li>
                        )}
                    </ul>
                )}
            </Form.Field>
            {buttonLocation === 'external' && (
                <Form.Submit asChild>
                    <Button
                        type="submit"
                        variant="primary" // Or a specific icon variant
                        className="autoSuggestInput__submitButton"
                        aria-label="Search"
                        icon='magnifying-glass'
                        iconPosition='after'
                    >
                        {buttonLabel}
                    </Button>
                </Form.Submit>
            )}
        </Form.Root>
    );
};

export default AutoSuggestInput;